import React, { useState } from 'react';
import Navigation from './Navigation';
import QuizFunnelComponent from './QuizFunnelComponent';
import SEO from './SEO';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Calendar,
  Play,
  MessageSquare,
  Users,
  Settings,
  ArrowRight,
  CheckCircle,
  Zap,
  Shield,
  Layers,
  Building2,
  Cpu,
  TrendingUp,
  Stethoscope,
  ShoppingBag,
  HardHat,
  GraduationCap,
  Landmark,
  X,
  BarChart3
} from 'lucide-react';
import { mockData } from '../utils/mock';

const Homepage = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Industry icon mapping
  const industryIcons = {
    'HR': Users,
    'Healthcare': Stethoscope, 
    'Retail & Hospitality': ShoppingBag,
    'Construction & Engineering': HardHat,
    'Education': GraduationCap,
    'Public Sector': Landmark
  };

  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioqbase-demo', '_blank');
  };

  const handleWatch = () => {
    setIsVideoModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">
      <SEO
        title="VioQbase - Voice-Powered AI Knowledge Base for Enterprise Teams"
        description="Transform your workplace efficiency with VioQbase's voice-activated AI knowledge base. Get instant answers, eliminate delays, and boost productivity for HR, IT, Healthcare, Finance, Legal, and University teams."
        keywords="AI knowledge base, voice AI, enterprise software, workplace efficiency, HR automation, IT support, healthcare AI, voice assistant, smart workplace, productivity tools, VioQbase, ROI calculator, assessment tool"
        canonical="https://vioqbase.com/"
        ogImage="https://vioqbase.com/images/vioqbase-homepage-og.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "VioQbase Homepage",
          "description": "Voice-powered AI knowledge base for enterprise teams",
          "url": "https://vioqbase.com/",
          "mainEntity": {
            "@type": "SoftwareApplication",
            "name": "VioQbase",
            "applicationCategory": "BusinessApplication",
            "description": "Voice-activated AI knowledge base platform for enterprise teams",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "url": "https://vioqbase.com/contact"
            }
          }
        }}
      />
      <Navigation />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-4 sm:px-6 py-20 lg:py-32 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 lg:space-y-8 animate-fade-in">
              <div className="space-y-4">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-7xl font-bold leading-tight tracking-tight">
                  Voice-Driven <span className="text-[#ff7a1f]">Answers</span>, Enterprise-Ready Knowledge.
                </h1>
                <p className="text-lg sm:text-xl lg:text-2xl text-blue-100 leading-relaxed">
                  VioQbase is your voice-activated knowledge base that delivers instant, accurate answers from your HR policies, SOPs, and internal docs — so your team gets what they need without delay.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 max-w-md sm:max-w-none">
                <Button 
                  size="lg" 
                  className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-semibold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl w-full sm:w-auto min-h-[44px]"
                  onClick={handleDemo}
                >
                  <Calendar className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Book a Demo
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#112d56] font-semibold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-xl transition-all duration-300 w-full sm:w-auto min-h-[44px]"
                  onClick={handleWatch}
                >
                  <Play className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Watch Overview
                </Button>
              </div>
            </div>
            
            {/* Hero Image/Animation */}
            <div className="relative lg:mt-0 mt-12">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vioq-error-fix/artifacts/fc02z8na_VioQbase%20Annimation%20New.gif" 
                  alt="VioQbase Voice Animation" 
                  className="w-full max-w-lg mx-auto lg:max-w-none h-auto rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -inset-4 bg-gradient-to-r from-[#ff7a1f]/20 to-blue-500/20 rounded-3xl blur-2xl opacity-30 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <p className="text-center text-gray-800 mb-8 text-lg sm:text-xl lg:text-2xl font-bold max-w-4xl mx-auto leading-relaxed">
            VioQbase is a versatile AI Voice powered solution that can be used across a wide range of industries.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 lg:gap-8 opacity-60">
            {mockData.trustLogos.map((industry, index) => {
              const IconComponent = industryIcons[industry] || Building2;
              return (
                <div key={index} className="flex items-center space-x-2 px-2 py-1">
                  <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500 flex-shrink-0" />
                  <span className="text-sm sm:text-base lg:text-lg font-semibold text-gray-700 whitespace-nowrap">{industry}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Welcome Video Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Welcome to <span className="text-[#ff7a1f]">VioQbase</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover how VioQbase transforms the way teams access knowledge with instant, accurate, voice-powered answers.
              </p>
            </div>

            <div className="relative">
              {/* Background decorative elements */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[#ff7a1f]/20 to-[#112d56]/20 rounded-3xl blur-2xl opacity-30"></div>
              <div className="absolute -inset-2 bg-gradient-to-r from-[#ff7a1f]/10 to-[#112d56]/10 rounded-2xl blur-xl"></div>
              
              {/* Video Container */}
              <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                {/* Video Player Header */}
                <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] px-6 py-4 flex items-center space-x-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <div className="flex-1 text-center"></div>
                  <div className="flex items-center space-x-2">
                    <Play className="h-4 w-4 text-white/70" />
                  </div>
                </div>
                
                {/* Video Embed */}
                <div className="bg-black relative">
                  <div className="relative" style={{ paddingBottom: '56.25%' }}>
                    <iframe 
                      src="https://api.vadoo.tv/iframe_test?id=2kteX6RKgZH8OfjYvUaKRC07L0bEdUTS" 
                      scrolling="no" 
                      style={{ 
                        border: 0, 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '100%', 
                        position: 'absolute', 
                        overflow: 'hidden',
                        borderRadius: '5px'
                      }} 
                      allowFullScreen="1" 
                      allow="autoplay"
                      title="Welcome to VioQbase Video"
                    />
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-6 -left-6 w-12 h-12 bg-[#ff7a1f]/20 rounded-full blur-xl"></div>
              <div className="absolute -bottom-6 -right-6 w-16 h-16 bg-[#112d56]/20 rounded-full blur-xl"></div>
            </div>

            <div className="text-center mt-12">
              <p className="text-lg text-gray-600 mb-6">
                Ready to see how VioQbase can transform your team's productivity?
              </p>
              <div className="flex justify-center">
                <Button 
                  size="lg"
                  className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-semibold px-8 py-4 text-lg rounded-xl transition-all duration-300 transform hover:scale-105"
                  onClick={handleDemo}
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Schedule Your Demo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quiz Funnel Section */}
      <section id="assessment" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#ff7a1f]/10 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#112d56]/10 rounded-full blur-3xl opacity-40"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Quiz Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#ff7a1f] to-[#e6681b] rounded-2xl mb-6 shadow-lg">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                VioQbase Value Check <span className="text-[#ff7a1f]">Assessment</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Get a personalized assessment of how much time and money VioQbase can save your company in just 2 minutes
              </p>
              <div className="flex flex-wrap justify-center items-center gap-6 mt-8 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>2-minute assessment</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Instant results</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>No commitment required</span>
                </div>
              </div>
            </div>

            {/* Quiz Component */}
            <QuizFunnelComponent />
          </div>
        </div>
      </section>

      {/* Why Teams Love VioQbase Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Why Teams Love <span className="text-[#ff7a1f]">VioQbase</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover what makes VioQbase the preferred choice for modern teams who need answers fast.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Card 1 */}
            <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 border border-gray-100">
              <div className="text-center space-y-4">
                <div className="text-4xl mb-4">📣</div>
                <h3 className="text-lg font-bold text-[#112d56] leading-tight">
                  Make Your Knowledge Speak
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Turn static HR documents into dynamic voice-driven conversations. No more buried PDFs or confusing portals — employees just call and ask.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 border border-gray-100">
              <div className="text-center space-y-4">
                <div className="text-4xl mb-4">⚡️</div>
                <h3 className="text-lg font-bold text-[#112d56] leading-tight">
                  Goodbye Tickets. Hello Instant Answers.
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  VioQbase eliminates the need for helpdesk backlogs. Most queries are resolved immediately with voice-activated precision.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 border border-gray-100">
              <div className="text-center space-y-4">
                <div className="text-4xl mb-4">🕒</div>
                <h3 className="text-lg font-bold text-[#112d56] leading-tight">
                  Available When You're Not
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Your HR assistant shouldn't clock out. VioQbase works 24/7, providing consistent support on holidays, weekends, and overnight.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 border border-gray-100">
              <div className="text-center space-y-4">
                <div className="text-4xl mb-4">🎙️</div>
                <h3 className="text-lg font-bold text-[#112d56] leading-tight">
                  No Training Required
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  If you can speak, you can use VioQbase. No logins, no passwords, no portal confusion — just instant, natural help.
                </p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 border border-gray-100">
              <div className="text-center space-y-4">
                <div className="text-4xl mb-4">📈</div>
                <h3 className="text-lg font-bold text-[#112d56] leading-tight">
                  Smarter Every Week
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  With built-in analytics, VioQbase helps you identify trending questions, update answers instantly, and improve HR operations continuously.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Stop Searching. Start <span className="text-[#ff7a1f]">Knowing</span>.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When your team struggles to find information, productivity suffers. VioQbase empowers them with fast, accurate, voice-driven answers — anytime.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <CardHeader className="text-center pb-2">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-[#112d56]">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription className="text-gray-600 text-lg leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Built for Every Team
            </h2>
            <p className="text-xl text-gray-600">
              Whether you're in HR, IT, or the C-suite, VioQbase adapts to your workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.useCases.map((useCase, index) => (
              <div key={index} className="group">
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <div className="h-3 bg-gradient-to-r from-[#ff7a1f] to-[#ff7a1f]/70"></div>
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-3">
                      <useCase.icon className="h-6 w-6 text-[#ff7a1f]" />
                      <CardTitle className="text-xl font-bold text-[#112d56]">{useCase.title}</CardTitle>
                    </div>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      {useCase.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {useCase.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Simple as <span className="text-[#ff7a1f]">1-2-3</span>
            </h2>
            <p className="text-xl text-gray-600">
              From setup to answers in days, not months.
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-[#ff7a1f]/30 via-[#ff7a1f] to-[#ff7a1f]/30 transform -translate-y-1/2"></div>
            
            <div className="grid lg:grid-cols-3 gap-12 relative">
              {mockData.howItWorks.map((step, index) => (
                <div key={index} className="text-center group">
                  <div className="relative inline-block mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#ff7a1f] to-[#e6681b] rounded-full flex items-center justify-center shadow-xl transform transition-all duration-300 group-hover:scale-110">
                      <step.icon className="h-10 w-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#112d56] text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-[#112d56] mb-4">{step.title}</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
        <div className="relative container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-6xl font-bold leading-tight">
              From Lost Time to <span className="text-[#ff7a1f]">Instant Clarity</span>
            </h2>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
              Eliminate your information bottlenecks and empower your workforce with VioQbase.
            </p>
            
            <div className="flex justify-center pt-8">
              <Button 
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-12 py-6 text-xl rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
                onClick={handleDemo}
              >
                <Calendar className="mr-3 h-6 w-6" />
                Book Your Demo Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Updated to match VioQbase design */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vioq-content-update/artifacts/w40y3694_Smart%20AI%20Logo%20white.png"
                  alt="Smart AI Solutions"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                Driving efficiency and customer excellence with VioSuite — AI-powered voice & knowledge solutions for modern business.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Product</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/products/vioreceptionist" className="hover:text-[#ff7a1f] transition-colors text-lg">VioReceptionist</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
                <li><a href="https://cal.com/smartaisolutions/vioqbase-demo" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff7a1f] transition-colors text-lg">Book Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-lg">
              © 2025 VioQbase. All rights reserved.
            </p>
            <div className="flex space-x-8 text-blue-200 mt-4 md:mt-0">
              <a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a>
              <a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl mx-auto">
            {/* Close Button */}
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#ff7a1f] transition-colors z-10"
              aria-label="Close video"
            >
              <X className="h-8 w-8" />
            </button>
            
            {/* Video Container */}
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white p-4">
                <h3 className="text-xl font-bold">VioQbase Knowledge Base Overview</h3>
                <p className="text-blue-200 text-sm">See how VioQbase transforms workplace efficiency</p>
              </div>
              
              {/* Vadoo.tv Embed */}
              <div className="relative">
                <div style={{ left: 0, width: '100%', height: 0, position: 'relative', paddingBottom: '56.25%' }}>
                  <figure style={{ 
                    left: 0, 
                    width: '100%', 
                    height: 0, 
                    position: 'relative', 
                    paddingBottom: '56.25%', 
                    marginBlockEnd: 0, 
                    marginBlockStart: 0, 
                    marginInlineStart: 0, 
                    marginInlineEnd: 0 
                  }}>
                    <iframe 
                      src="https://api.vadoo.tv/iframe_test?id=14BBjeJiKbuJKYLktJMyIiBkC3s6cVk7" 
                      scrolling="no" 
                      style={{ 
                        border: 0, 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '100%', 
                        position: 'absolute', 
                        overflow: 'hidden', 
                        borderRadius: '0 0 8px 8px' 
                      }} 
                      allowFullScreen="1" 
                      allow="autoplay"
                      title="VioQbase Overview Video"
                    />
                  </figure>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Homepage;